import React, { useState } from 'react';

const MAX_VISIBLE = 5;

// variant: 'chance' | 'caution' | 'risk'
const SIGNAL_STYLES = {
  chance:  { bg: '#E8F7E9', border: '#1FA534', icon: '#1FA534' },
  caution: { bg: '#FEF8EC', border: '#F0C045', icon: '#F0C045' },
  risk:    { bg: '#FDECEC', border: '#E83838', icon: '#E83838' },
};

function SignalBadge({ signal, variant }) {
  const style = SIGNAL_STYLES[variant] || SIGNAL_STYLES.risk;

  return (
    <div
      className="flex items-start gap-3 rounded-md py-2 px-3"
      style={{ background: style.bg, borderLeft: `3px solid ${style.border}` }}
    >
      <span className="text-[16px] shrink-0 mt-0.5" style={{ color: style.icon }}>
        {variant === 'chance' ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2l1.5 3.5H13l-2.8 2.2 1 3.5L8 9.3 4.8 11.2l1-3.5L3 5.5h3.5z" fill="currentColor"/></svg>
        ) : variant === 'caution' ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/><rect x="7.25" y="4.5" width="1.5" height="4" rx="0.75" fill="currentColor"/><circle cx="8" cy="10.5" r="0.75" fill="currentColor"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1.5L1 14h14L8 1.5z" stroke="currentColor" strokeWidth="1.5" fill="none"/><text x="8" y="12" textAnchor="middle" fill="currentColor" fontSize="9" fontWeight="700">!</text></svg>
        )}
      </span>
      <span className="text-[14px] text-[#4A4A52] flex-1">{signal.label}</span>
    </div>
  );
}

const EMPTY_LABELS = {
  chance:  'Chancen',
  caution: 'Hinweise',
  risk:    'Risiken',
};

function SignalColumn({ title, titleColor, signals, variant }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? signals : signals.slice(0, MAX_VISIBLE);
  const remaining = signals.length - MAX_VISIBLE;

  return (
    <div className="flex-1 min-w-0">
      <h4 className="text-[16px] font-semibold mb-2" style={{ color: titleColor }}>
        {title}
      </h4>
      <div className="space-y-1.5">
        {visible.map((s, i) => (
          <SignalBadge key={i} signal={s} variant={variant} />
        ))}
      </div>
      {remaining > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="mt-2 text-[14px] font-medium text-[#1DB3B3] hover:text-[#0E7A6B] transition-colors"
        >
          + {remaining} weitere
        </button>
      )}
      {signals.length === 0 && (
        <p className="text-[13px] text-[#B0B0B8] italic">Keine {EMPTY_LABELS[variant]} erkannt.</p>
      )}
    </div>
  );
}

export default function SignalsPanel({ signals }) {
  if (!signals?.length) return null;

  const chances  = signals.filter(s => s.type === 'positive');
  const cautions = signals.filter(s => s.type === 'caution');
  const risks    = signals.filter(s => s.type === 'risk');

  // Spalte nur rendern wenn mindestens ein Signal vorhanden
  const columns = [
    { key: 'chance',  title: 'Chancen',  color: '#1FA534', signals: chances },
    { key: 'caution', title: 'Hinweise', color: '#F0C045', signals: cautions },
    { key: 'risk',    title: 'Risiken',  color: '#E83838', signals: risks },
  ].filter(col => col.signals.length > 0);

  // Fallback: alle Spalten anzeigen wenn alle leer (sollte durch äußeres if nicht passieren)
  const displayColumns = columns.length > 0 ? columns : [
    { key: 'chance',  title: 'Chancen',  color: '#1FA534', signals: [] },
    { key: 'caution', title: 'Hinweise', color: '#F0C045', signals: [] },
    { key: 'risk',    title: 'Risiken',  color: '#E83838', signals: [] },
  ];

  return (
    <div className="bg-white border border-[#E0E0DF] rounded-xl p-4">
      <h3 className="text-[20px] font-semibold text-[#1F1F1F] mb-3">Signale</h3>
      <div className="flex gap-4">
        {displayColumns.map(col => (
          <SignalColumn
            key={col.key}
            title={col.title}
            titleColor={col.color}
            signals={col.signals}
            variant={col.key}
          />
        ))}
      </div>
    </div>
  );
}
